import { Trans, t } from "@web-monorepo/i18n/macro";
import { DDSButton, DDSDisplayText, DDSIconButton } from "@web-monorepo/dds";
import { IconChevronLeft, IconPause, IconPlay } from "@web-monorepo/dds-icons";
import { playStates } from "hooks/useAudioPlayer";
import { ReactNode, useCallback } from "react";
import { closeWebview } from "src/pods/webviewInit";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { formatTime } from "utils";
import styles from "./ToolkitAudioActivityChrome.module.css";

type ToolkitAudioActivityChromeProps = {
  play: VoidFunction;
  pause: VoidFunction;
  playState: string;
  secondsRemaining: number;
  mainContent: ReactNode;
  closeAndGivePoints: VoidFunction;
  loggingId: string;
};

/**
 * Presentation component that renders standard visuals for an audio activity such as
 * time remaining, and action buttons.
 *
 * Actual audio playing and state management should be handled by the parent
 */
export default function ToolkitAudioActivityChrome({
  play,
  pause,
  playState,
  secondsRemaining,
  mainContent,
  closeAndGivePoints,
  loggingId,
}: ToolkitAudioActivityChromeProps) {
  const closeActivityAndLogEvent = useCallback(
    (logAction: string) => {
      logParentToolkitEvent({ eventName: `${loggingId}.${logAction}` });
      // for iOS native toolkit landing page
      // handling on the iOS side for this action does not exist on older clients
      // so shouldn't affect toolkit with the webview landing page
      closeWebview();
      window.history.back();
    },
    [loggingId],
  );
  return (
    <div className={styles.container}>
      <DDSIconButton
        data-name="toolkit_audio_activity_chrome:close"
        aria-label={t`Go back`}
        tooltip={{
          content: t`Go back`,
          dataName: "toolkit_audio_activity_chrome:close:tooltip",
        }}
        variant="transparent"
        className={styles.closeButton}
        onClick={() => closeActivityAndLogEvent("close.tap")}
      >
        <IconChevronLeft />
      </DDSIconButton>

      <div className={styles.titleRow}>
        <DDSDisplayText as="h1" level="md" className={styles.title}>
          {titleText(playState, secondsRemaining)}
        </DDSDisplayText>
      </div>

      <div className={styles.imageWrap}>{mainContent}</div>

      {playState == playStates.READY && (
        <div className={styles.buttonRow}>
          <DDSButton
            data-name="toolkit_audio_activity_chrome:brush_teeth:go"
            variant="primarySubtle"
            size="large"
            width="fill"
            onClick={() => {
              logParentToolkitEvent({ eventName: `${loggingId}.start` });
              play();
            }}
          >
            <Trans>Let's go!</Trans>
          </DDSButton>
        </div>
      )}

      {(playState == playStates.PLAYING || playState == playStates.PAUSED) && (
        <div className={styles.buttonRow}>
          {playState === playStates.PLAYING ? (
            <DDSIconButton
              data-name="toolkit_audio_activity_chrome:pause_button"
              aria-label={t`Pause`}
              tooltip={{
                content: t`Pause`,
                dataName: "toolkit_audio_activity_chrome:pause_button:tooltip",
              }}
              variant="white"
              size="large"
              onClick={() => pause()}
            >
              <IconPause />
            </DDSIconButton>
          ) : (
            <DDSIconButton
              data-name="toolkit_audio_activity_chrome:play_button"
              aria-label={t`Play`}
              tooltip={{
                content: t`Play`,
                dataName: "toolkit_audio_activity_chrome:play_button:tooltip",
              }}
              variant="white"
              size="large"
              onClick={() => play()}
            >
              <IconPlay />
            </DDSIconButton>
          )}
        </div>
      )}

      {playState == playStates.DONE && (
        <div className={styles.doneRow}>
          <DDSButton
            data-name="toolkit_audio_activity_chrome:brush_teeth:give_points"
            variant="primarySubtle"
            size="large"
            width="fill"
            onClick={() => {
              logParentToolkitEvent({ eventName: `${loggingId}.give_points.tap` });
              closeAndGivePoints?.();
            }}
          >
            <Trans>Give points</Trans>
          </DDSButton>
          <DDSButton
            data-name="toolkit_audio_activity_chrome:done"
            variant="tertiary"
            size="large"
            width="fill"
            className={styles.doneButton}
            onClick={() => closeActivityAndLogEvent("done.tap")}
          >
            <Trans>Done</Trans>
          </DDSButton>
        </div>
      )}
    </div>
  );
}

function titleText(
  playState: ToolkitAudioActivityChromeProps["playState"],
  secondsRemaining: ToolkitAudioActivityChromeProps["secondsRemaining"],
) {
  switch (playState) {
    case playStates.READY:
      return t`Are you ready?`;
    case playStates.DONE:
      return t`Good job!`;
    default:
      return formatTime(secondsRemaining);
  }
}
